import React, { useState, useCallback, useRef } from 'react';
import { Button } from './button';
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUploader Component
 * 
 * A drag & drop image upload component with preview support.
 * 
 * Props:
 * - value: Current image URL (for controlled component)
 * - onChange: Callback when image URL changes
 * - folder: Subfolder to upload to (default: "products")
 * - placeholder: Placeholder text
 * - multiple: Allow multiple uploads (returns array of URLs)
 * - maxFiles: Maximum files for multiple upload (default: 5)
 * - className: Additional CSS classes
 */
export function ImageUploader({ 
  value, 
  onChange, 
  folder = "products",
  placeholder = "Drop an image here or click to upload",
  multiple = false,
  maxFiles = 5,
  className = ""
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  // Upload a single file
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await fetch(`${API_URL}/api/image-upload/image`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Upload failed');
    }

    const data = await response.json();
    // Return full URL
    return `${API_URL}${data.url}`;
  };

  // Handle file drop
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null);

    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    );

    if (files.length === 0) {
      setError('Please drop image files only');
      return;
    }

    await handleFiles(files);
  }, [folder, multiple, maxFiles, onChange]);

  // Handle file selection from input
  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      await handleFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [folder, multiple, maxFiles, onChange]);

  // Process files
  const handleFiles = async (files) => {
    setError(null);
    setUploading(true);

    try {
      if (multiple) {
        // Limit number of files
        const filesToUpload = files.slice(0, maxFiles);
        setUploadProgress(`Uploading ${filesToUpload.length} files...`);

        const urls = [];
        for (let i = 0; i < filesToUpload.length; i++) {
          setUploadProgress(`Uploading ${i + 1} of ${filesToUpload.length}...`);
          const url = await uploadFile(filesToUpload[i]);
          urls.push(url);
        }

        // Append to existing values if any
        const currentUrls = Array.isArray(value) ? value : [];
        onChange([...currentUrls, ...urls]);
      } else {
        // Single file upload
        setUploadProgress('Uploading...');
        const url = await uploadFile(files[0]);
        onChange(url);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  // Remove image
  const handleRemove = useCallback((urlToRemove) => {
    if (multiple) {
      const currentUrls = Array.isArray(value) ? value : [];
      onChange(currentUrls.filter(url => url !== urlToRemove));
    } else {
      onChange('');
    }
  }, [multiple, value, onChange]);

  // Click to open file dialog
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Render preview for single image
  const renderSinglePreview = () => {
    if (!value) return null;

    return (
      <div className="relative group">
        <img 
          src={value} 
          alt="Uploaded" 
          className="w-full h-40 object-cover rounded-lg border"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRemove(value);
          }}
          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-xs text-white bg-black/50 rounded px-2 py-1 truncate">
            {value.split('/').pop()}
          </p>
        </div>
      </div>
    );
  };

  // Render gallery for multiple images
  const renderGalleryPreview = () => {
    const urls = Array.isArray(value) ? value : [];
    if (urls.length === 0) return null;

    return (
      <div className="grid grid-cols-3 gap-2 mb-3">
        {urls.map((url, idx) => (
          <div key={idx} className="relative group aspect-square">
            <img 
              src={url} 
              alt={`Gallery ${idx + 1}`} 
              className="w-full h-full object-cover rounded-lg border"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(url);
              }}
              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  // Check if we have content
  const hasValue = multiple 
    ? (Array.isArray(value) && value.length > 0) 
    : !!value;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Preview */}
      {!multiple && hasValue && renderSinglePreview()}
      {multiple && renderGalleryPreview()}

      {/* Upload Zone */}
      {(!hasValue || multiple) && (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            transition-colors duration-200
            ${isDragging 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }
            ${uploading ? 'pointer-events-none opacity-60' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple={multiple}
            onChange={handleFileSelect}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-600">{uploadProgress}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className={`p-3 rounded-full ${isDragging ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <Upload className={`h-6 w-6 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              </div>
              <p className="text-sm text-gray-600">{placeholder}</p>
              <p className="text-xs text-gray-400">
                PNG, JPG, GIF, WebP up to 10MB
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Or use URL input */}
      {!multiple && !hasValue && !uploading && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="flex-1 border-t"></span>
          <span>or paste an image URL above</span>
          <span className="flex-1 border-t"></span>
        </div>
      )}
    </div>
  );
}

export default ImageUploader;
